import { useEffect, useState } from "react";

import { useNotifications } from "./NotificationsContext";
import { fetchTransfers } from "./api";

/* ------------------------------------------------------------------ *
 * How many work-order transfers are waiting on a decision from us —
 * the badge on the Transfers nav item.
 *
 * There is no count endpoint, and the list is small and unpaged, so the
 * length of `?direction=incoming&status=pending` is the count. It is
 * refetched when the open business changes and whenever the notification
 * poll sees something new (a request raises a notification, so the two
 * move together) — no extra timer of its own.
 *
 * Deciding a transfer changes the count without changing anything the poll
 * watches, so the screens that decide one call `transfersChanged()` and the
 * badge refetches straight away instead of lagging a poll behind.
 * ------------------------------------------------------------------ */

const EVENT = "rf:transfers-changed";

/** Tell every mounted badge that a transfer was requested or decided. */
export const transfersChanged = () => {
  try {
    window.dispatchEvent(new Event(EVENT));
  } catch (_) {
    /* no window (tests) — the next poll picks the change up */
  }
};
export function useIncomingTransferCount(enabled = true) {
  const notif = useNotifications() || {};
  const { businessId, unviewedCount, version } = notif;
  const [count, setCount] = useState(0);
  const [bump, setBump] = useState(0);

  useEffect(() => {
    const onChanged = () => setBump((n) => n + 1);
    window.addEventListener(EVENT, onChanged);
    return () => window.removeEventListener(EVENT, onChanged);
  }, []);

  useEffect(() => {
    if (!enabled || !businessId) {
      setCount(0);
      return undefined;
    }
    let cancelled = false;
    fetchTransfers({ direction: "incoming", status: "pending" })
      .then((list) => !cancelled && setCount(Array.isArray(list) ? list.length : 0))
      .catch(() => !cancelled && setCount(0));
    return () => {
      cancelled = true;
    };
  }, [enabled, businessId, unviewedCount, version, bump]);

  return count;
}

export default useIncomingTransferCount;
